<script lang="ts">
  import { tick, onMount, getContext } from 'svelte';
  import { Text } from '@squareup/public-web-design-system';
  import { DesignSystem as DesignSystemEnum } from '$lib/helpers/design-system';

  let isShowing: boolean = false;
  let isError: boolean = false;
  let displayedMessage: string = '';
  let scrollbarWidth: number = 0;

  let flashBanner: HTMLDivElement;

  const isPWDSEnabled = getContext<DesignSystemEnum>('designSystem') === DesignSystemEnum.PublicWebDesignSystem;

  export const hideFlashBanner = () => {
    isShowing = false;
  };

  export const showError = (message: string) => {
    isError = true;
    displayedMessage = message;
    isShowing = true;
  };

  export const showSuccess = (message: string, duration: number) => {
    isError = false;
    displayedMessage = message;
    isShowing = true;

    setTimeout(() => {
      hideFlashBanner();
    }, duration);
  };

  onMount(async () => {
    document.body.appendChild(flashBanner);

    // Need to add scrollbar width to close button right positioning
    await tick();
    scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  });
</script>

<div
  class="flashBanner"
  bind:this={flashBanner}
  class:isShowing
  style:background-color={isError ? 'var(--color-red)' : 'var(--color-green)'}
  style:--scrollbar-width={`${scrollbarWidth}px`}
  role="status"
  aria-live="polite"
>
  {#if isPWDSEnabled}
    <Text variant="100">
      {@html displayedMessage}
    </Text>
  {:else}
    <div class="flashBannerText">
      {@html displayedMessage}
    </div>
  {/if}
  <button on:click={hideFlashBanner}>Close</button>
</div>

<style lang="scss">
  @layer standard-components {
    .flashBanner {
      visibility: hidden;
      transform: translateY(-100%);
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      padding: var(--space-20) 50px;
      z-index: 10000;
      font-size: var(--font-size-14);
      line-height: var(--line-height-24);
      font-weight: var(--font-weight-bold);
      text-align: center;
      box-shadow: 0 4px 20px var(--color-drop-shadow);
      color: white;
      background-color: var(--color-green);

      transition:
        transform var(--duration-leave) var(--easing-accelerate) 0s,
        visibility 0s linear var(--duration-leave);

      &.isShowing {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
        transition:
          transform var(--duration-enter) var(--easing-decelerate) 0s,
          visibility 0s linear 0s;
      }

      button {
        appearance: none;
        cursor: pointer;
        border-color: transparent;
        position: absolute;
        display: block;
        top: var(--space-20);
        right: calc(var(--space-20) + var(--scrollbar-width));
        width: 24px;
        height: 24px;
        color: transparent;
        background-color: transparent !important;
        background-repeat: no-repeat;
        background-position: center;
        outline: 0;

        //TODO: Move to SVG
        background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTQuNzA3IDMuMjkzYTEgMSAwIDEgMC0xLjQxNCAxLjQxNEwxMC41ODYgMTJsLTcuMjkzIDcuMjkzYTEgMSAwIDEgMCAxLjQxNCAxLjQxNEwxMiAxMy40MTRsNy4yOTMgNy4yOTNhMSAxIDAgMSAwIDEuNDE0LTEuNDE0TDEzLjQxNCAxMmw3LjI5My03LjI5M2ExIDEgMCAxIDAtMS40MTQtMS40MTRMMTIgMTAuNTg2IDQuNzA3IDMuMjkzeiIgZmlsbD0iI2ZmZiIvPgo8L3N2Zz4K);
      }

      // PWDS specific styling
      :global(.Text) {
        --max-width: none;
        --color: var(--color-white);
      }
    }
  }
</style>
